// One mapping from a failed request to the sentence a person reads.
//
// Server text is shown only for validation answers (400/422), where it is
// written for the user. Everything else reads as fixed copy: a 404 from an API
// that does not have the route yet, the SPA fallback answering an API path with
// a build instruction, a stack trace in a 500, or axios' "Request failed with
// status code 404" must never reach the screen.

const MAX_SERVER_TEXT = 160;

const capitalise = (text) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** True when a 2xx answer is not the JSON an API call expects (an HTML page). */
export const isNonJsonBody = (data) =>
  typeof data === "string" && /^\s*</.test(data);

const serverText = (data) => {
  const value = data?.error?.message ?? data?.error ?? data?.message;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > MAX_SERVER_TEXT || /^</.test(text)) return null;
  return text;
};

// A service that already turned a failure into a sentence throws a plain
// Error with no status; that sentence is kept. An axios error never is.
const serviceSentence = (error) => {
  if (!(error instanceof Error) || error.isAxiosError || error.response) return null;
  if (error.status != null) return null;
  const text = (error.message || "").trim();
  if (!text || text.length > MAX_SERVER_TEXT || /^</.test(text) || /status code \d{3}/i.test(text)) return null;
  if (text === "Network Error" || /^timeout/i.test(text)) return null;
  return text;
};

/**
 * Plain copy for a failed load.
 * @param {unknown} error axios error, or an Error that already carries `userMessage`
 * @param {string} subject what failed, lower case: "your day", "the floor", "users"
 */
export const loadErrorMessage = (error, subject = "this view") => {
  if (error && typeof error === "object" && typeof error.userMessage === "string") {
    return error.userMessage;
  }
  const kept = serviceSentence(error);
  if (kept) return kept;
  const status = error?.response?.status ?? error?.status;
  const sentence = `${capitalise(subject)} could not be loaded.`;
  if (status === 401) return "Your session ended. Sign in again to continue.";
  if (status === 403) return "You don't have access to this view.";
  if (status === 404) return "This view is not available on this server yet.";
  if (status === 400 || status === 422) {
    return serverText(error?.response?.data) || sentence;
  }
  if (status === 429) return `${sentence} Too many requests; try again in a minute.`;
  return `${sentence} Try again in a moment.`;
};

/** Wrap a failure so callers (useLoad, CardError) can render `message` as is. */
export const toLoadError = (error, subject) => {
  const wrapped = new Error(loadErrorMessage(error, subject));
  wrapped.userMessage = wrapped.message;
  wrapped.status = error?.response?.status ?? error?.status;
  wrapped.cause = error;
  return wrapped;
};

/** A 2xx that is not JSON (index.html from a SPA fallback) is a failure too. */
export const assertJson = (response) => {
  if (isNonJsonBody(response?.data)) {
    const error = new Error("Expected JSON from the API");
    error.status = 404;
    throw error;
  }
  return response;
};

/**
 * Plain copy for any failed request (reads and writes): validation text is
 * kept, everything else becomes `fallback`. Use where loadErrorMessage's
 * "could not be loaded" wording would be wrong.
 */
export const requestErrorMessage = (error, fallback) => {
  if (error && typeof error === "object" && typeof error.userMessage === "string") {
    return error.userMessage;
  }
  const kept = serviceSentence(error);
  if (kept) return kept;
  const status = error?.response?.status ?? error?.status;
  if (status === 401) return "Your session ended. Sign in again to continue.";
  if (status === 403) return "You don't have access to this.";
  if (status === 400 || status === 409 || status === 422) {
    return serverText(error?.response?.data) || fallback;
  }
  return fallback;
};
